import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import SafeMarkdown from '../../Common/SafeMarkdown';
import remarkGfm from 'remark-gfm';
import { formatDramaForMarkdown } from './assessmentsFormatUtils';
import { isAnswerObject, type AssessmentResponse, type Question } from './assessmentsTypes';
import PassageIllustrations from './PassageIllustrations';

export interface AssessmentResultsDisplayProps {
  assessmentResponse: AssessmentResponse;
  generationStreamingQuestions: { [passageNumber: number]: string };
  generationStreamingCombinedQuestions: string;
}

const AssessmentResultsDisplay: React.FC<AssessmentResultsDisplayProps> = ({
  assessmentResponse,
  generationStreamingQuestions,
  generationStreamingCombinedQuestions,
}) => (
<Paper elevation={3} sx={{ p: 4, mb: 3 }}>
  {(() => {
    let questionCounter = 0;
    return (
      <>
        {assessmentResponse.passages?.map((passage, index) => (
          <Box key={index} sx={{ 
            mb: index === 0 ? 6 : 4, // More space between passages
            maxWidth: '900px',
            margin: '0 auto',
            px: 4,

            '& h1': {
              textAlign: 'center',
              marginBottom: '2em',
              color: '#2c5282',
              fontWeight: 'bold',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
              fontSize: '1.25rem !important',
              lineHeight: '1.2',
              paddingLeft: 0,
              paddingRight: 0
            },
            '& h2, & h3': {
              textAlign: 'center',
              marginBottom: '2em',
              color: '#2c5282',
              fontWeight: 'bold',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
              paddingLeft: 0,
              paddingRight: 0
            },
            '& p': {
              textAlign: passage.genre === 'Poetry' ? 'center' : 'left',
              textIndent: '0',
              lineHeight: '1.8',
              marginBottom: '1em',
              maxWidth: '85ch',
              marginLeft: passage.genre === 'Poetry' ? 'auto' : (((passage.genre || '').toLowerCase().includes('drama')) ? 'auto' : '4em'),
              marginRight: '4em'
            }
          }}>
            {/* Show final passage content with or without illustrations */}
            {(() => {
              // Debug logging
              if (passage.illustrations && passage.illustrations.length > 0) {
                console.log('Passage illustrations check:', {
                  hasIllustrations: !!passage.illustrations,
                  count: passage.illustrations.length,
                  illustrations: passage.illustrations.map((img, idx) => ({
                    index: idx,
                    fullObject: img,
                    imageGenerated: img.imageGenerated,
                    hasImageUrl: !!img.imageUrl,
                    imageUrlType: typeof img.imageUrl,
                    imageUrlLength: img.imageUrl ? img.imageUrl.length : 0,
                    imageUrlPreview: img.imageUrl ? img.imageUrl.substring(0, 50) + '...' : 'none',
                    placementIndex: img.placementIndex
                  })),
                  willRender: passage.illustrations.some(img => img.imageGenerated && img.imageUrl),
                  renderCheck: passage.illustrations.map(img => ({
                        generated: img.imageGenerated,
                        hasUrl: !!img.imageUrl,
                        passes: img.imageGenerated && img.imageUrl
                  }))
                });
              }
              return passage.illustrations && passage.illustrations.length > 0 && passage.illustrations.some(img => img.imageGenerated && img.imageUrl);
            })() ? (
              /* If illustrations exist, use PassageIllustrations which handles inline display */
              <PassageIllustrations
                illustrations={passage.illustrations}
                passageText={passage.passage}
                passageTitle={passage.title}
                genre={passage.genre}
              />
            ) : (
              /* Otherwise, use SafeMarkdown for plain passage display */
              <SafeMarkdown>
                {(() => {
                  if (/drama/i.test(passage.genre || '')) {
                    return formatDramaForMarkdown(passage.passage, passage.title);
                  }
                  return `# ${passage.title}\n\n${passage.passage.replace(/^[^\n]*\n\n/, '')}`;
                })()}
              </SafeMarkdown>
            )}

            {/* Questions for this passage */}
            {passage.questions && passage.questions.length > 0 && (
              <Box sx={{ 
                mt: 5, // More space before questions
                maxWidth: '85ch',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{ 
                  mb: 2,
                  textAlign: 'center',
                  color: '#2c5282',
                  fontWeight: 'bold',
                  fontSize: '1.25rem',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '100%'
                }}>
                  Questions for Passage {index + 1}
                </Typography>
                
                {/* Show streaming or final questions content */}
                {generationStreamingQuestions[index + 1] ? (
                  <Box sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    maxWidth: '85ch',
                    marginLeft: 'auto',
                    marginRight: 'auto'
                  }}>
                    {generationStreamingQuestions[index + 1]}
                  </Box>
                ) : (
                  /* Show current questions only when not streaming */
                  passage.questions?.map((question) => {
                    questionCounter++;
                    return (
                      <div className="question-wrapper" key={questionCounter}>
                        <Box sx={{ 
                          mb: 4
                        }} className="question-block">
                          <Typography variant="body1" sx={{ 
                            fontWeight: 'bold',
                            mb: 2
                          }}>
                            {questionCounter}. {question.question}
                          </Typography>
                          <div className="answer-wrapper">
                            <Box sx={{ pl: 2 }} className="answer-choices">
                              {question.answers?.map((answer, aIndex) => {
                                const answerText = isAnswerObject(answer) ? answer.text : String(answer);
                                const cleanAnswer = answerText.replace(/^[A-D][\s]*[\.:)]\s*/i, '').trim();
                                
                                return (
                                  <Typography key={aIndex} sx={{ 
                                    mb: 1.5,
                                    display: 'flex',
                                    gap: 0.25,
                                    alignItems: 'flex-start'
                                  }}>
                                    <span style={{ width: '1.2em' }}>{String.fromCharCode(65 + aIndex)}.</span>
                                    <span>{cleanAnswer}</span>
                                  </Typography>
                                );
                              })}
                            </Box>
                          </div>
                        </Box>
                      </div>
                    );
                  })
                )}
              </Box>
            )}
          </Box>
        ))}

        {/* Combined Questions - show after all passages */}
        {assessmentResponse.isPairedPassage && (assessmentResponse.combinedQuestions || generationStreamingCombinedQuestions) && (
          <Box sx={{ 
            mt: 6, // More space before combined questions
            maxWidth: '85ch',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            <Typography variant="subtitle1" gutterBottom sx={{ 
              mb: 2,
              textAlign: 'center',
              color: '#2c5282',
              fontWeight: 'bold',
              fontSize: '1.25rem',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%'
            }}>
              Combined Questions
            </Typography>
            
            {/* Show streaming or final combined questions content */}
            {generationStreamingCombinedQuestions ? (
              <Box sx={{ 
                maxWidth: '85ch',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <SafeMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => (
                      <Typography sx={{ mb: 2, lineHeight: 1.6 }}>
                        {children}
                      </Typography>
                    ),
                    ol: ({ children }) => (
                      <Box component="ol" sx={{ pl: 0, listStyle: 'none' }}>
                        {children}
                      </Box>
                    ),
                    li: ({ children }) => {
                      const content = String(children);
                      // Check if this is a question (starts with number)
                      if (/^\d+\./.test(content)) {
                        return (
                          <Box sx={{ mb: 3 }} className="question-block">
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                              {children}
                            </Typography>
                          </Box>
                        );
                      }
                      // Check if this is an answer choice (A., B., C., D.)
                      if (/^[A-D]\./.test(content)) {
                        return (
                          <Typography sx={{ 
                            mb: 1,
                            pl: 2,
                            display: 'flex',
                            gap: 0.25,
                            alignItems: 'flex-start'
                          }}>
                            {children}
                          </Typography>
                        );
                      }
                      return <Typography sx={{ mb: 1 }}>{children}</Typography>;
                    }
                  }}
                >
                  {generationStreamingCombinedQuestions}
                </SafeMarkdown>
              </Box>
            ) : (
              /* Show current combined questions only when not streaming */
              assessmentResponse.combinedQuestions?.map((question, index) => {
                const currentQuestionNumber = questionCounter + index + 1;
                return (
                  <div className="question-wrapper" key={currentQuestionNumber}>
                    <Box sx={{ 
                      mb: 4
                    }} className="question-block">
                      <Typography variant="body1" sx={{ 
                        fontWeight: 'bold',
                        mb: 2
                      }}>
                        {currentQuestionNumber}. {question.question}
                      </Typography>
                      <div className="answer-wrapper">
                        <Box sx={{ pl: 2 }} className="answer-choices">
                          {question.answers?.map((answer, aIndex) => {
                            const answerText = isAnswerObject(answer) ? answer.text : String(answer);
                            const cleanAnswer = answerText.replace(/^[A-D][\s]*[\.:)]\s*/i, '').trim();
                            return (
                              <Typography key={aIndex} sx={{ 
                                mb: 1.5,
                                display: 'flex',
                                gap: 0.25,
                                alignItems: 'flex-start'
                              }}>
                                <span style={{ width: '1.2em' }}>{String.fromCharCode(65 + aIndex)}.</span>
                                <span>{cleanAnswer}</span>
                              </Typography>
                            );
                          })}
                        </Box>
                      </div>
                    </Box>
                  </div>
                );
              })
            )}
          </Box>
        )}

        {/* Answer Key Section */}
        {assessmentResponse.answerKey && (
          <Box sx={{ 
            mt: 6, // More space before answer key
            pt: 4, // More padding top
            borderTop: '2px solid #2c5282', // Stronger border
            maxWidth: '900px',
            margin: '0 auto',
            px: 4
          }}>
            <Typography variant="h6" gutterBottom sx={{ 
              mb: 3,
              textAlign: 'center',
              color: '#2c5282',
              fontWeight: 'bold',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%'
            }}>
              Answer Key
            </Typography>
            <Box sx={{ 
              maxWidth: '85ch',
              marginLeft: 'auto',
              marginRight: 'auto',
              '& p': {
                marginBottom: '1em',
                lineHeight: '1.6',
                marginLeft: '2em',
                marginRight: '2em'
              },
              '& h1, & h2, & h3': {
                textAlign: 'center',
                marginBottom: '1em',
                color: '#2c5282',
                fontWeight: 'bold',
                marginLeft: 'auto',
                marginRight: 'auto'
              },
              '& strong': {
                fontWeight: 'bold',
                color: '#2c5282'
              },
              '& em': {
                fontStyle: 'italic'
              },
              '& ul, & ol': {
                marginLeft: '2em',
                marginBottom: '1em'
              },
              '& li': {
                marginBottom: '0.5em'
              },
              '& blockquote': {
                borderLeft: '4px solid #2c5282',
                paddingLeft: '1em',
                marginLeft: 0,
                fontStyle: 'italic'
              }
            }}>
              {(() => {
                let answerKeyContent = assessmentResponse.answerKey;
                let questionCounter = 0;
                
                // Process passage questions
                assessmentResponse.passages.forEach((passage) => {
                  if (passage.questions) {
                    passage.questions.forEach(() => {
                      questionCounter++;
                      // Replace "Passage X - Question Y" or "Question Y" with sequential number
                      answerKeyContent = answerKeyContent.replace(
                        /(?:Passage \d+ - )?Question \d+/,
                        `${questionCounter}`
                      );
                    });
                  }
                });
                
                // Process combined questions
                if (assessmentResponse.combinedQuestions) {
                  assessmentResponse.combinedQuestions.forEach(() => {
                    questionCounter++;
                    // Replace "Combined Question X" with sequential number
                    answerKeyContent = answerKeyContent.replace(
                      /Combined Question \d+/,
                      `${questionCounter}`
                    );
                  });
                }
                
                return (
                  <SafeMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({children}) => (
                        <Typography 
                          variant="h4" 
                          component="h1" 
                          sx={{ 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#2c5282',
                            fontSize: '1.25rem',
                            marginBottom: '1em',
                            marginTop: '0',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            lineHeight: 1.2,
                            width: '100%'
                          }}
                        >
                          {children}
                        </Typography>
                      ),
                      h2: ({children}) => (
                        <Typography 
                          variant="h5" 
                          component="h2" 
                          sx={{ 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#2c5282',
                            fontSize: '1.1rem',
                            marginBottom: '1em',
                            marginTop: '1em',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            lineHeight: 1.2,
                            width: '100%'
                          }}
                        >
                          {children}
                        </Typography>
                      ),
                      h3: ({children}) => (
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          sx={{ 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#2c5282',
                            fontSize: '1rem',
                            marginBottom: '0.5em',
                            marginTop: '1em',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            lineHeight: 1.2,
                            width: '100%'
                          }}
                        >
                          {children}
                        </Typography>
                      ),
                      p: ({children}) => (
                        <Typography 
                          variant="body1" 
                          component="p" 
                          sx={{ 
                            marginBottom: '1em',
                            lineHeight: 1.6
                          }}
                        >
                          {children}
                        </Typography>
                      ),
                      strong: ({children}) => (
                        <Box component="span" sx={{ fontWeight: 'bold', color: '#2c5282' }}>
                          {children}
                        </Box>
                      ),
                      em: ({children}) => (
                        <Box component="span" sx={{ fontStyle: 'italic' }}>
                          {children}
                        </Box>
                      )
                    }}
                  >
                    {answerKeyContent}
                  </SafeMarkdown>
                );
              })()}
            </Box>
          </Box>
        )}
      </>
    );
  })()}
</Paper>
);

export default AssessmentResultsDisplay;
